import { useQuery } from '@tanstack/react-query';

import { AssignmentGatewayMessage } from '../../../../../types/AssignmentGatewayMessage';
import { AssignmentStatusBadge } from '../../../../assignment/common/components/AssignmentStatusBadge';
import { AssignmentSupplier } from '../../../../assignment/components/AssignmentSupplier';
import { StatePlaceholder } from '../../../../cdl/StatePlaceholder/StatePlaceholder';
import { TableBuilder } from '../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../cdl/TableBuilder/TableBuilderColumn';
import { Text } from '../../../../cdl/Text/Text';
import { queries } from '../../../../common/api/queryKeys/queries';
import { AdvancedError } from '../../../../common/Error/AdvancedError';
import { translate } from '../../../../common/helpers/translate.helper';
import { IconListDetails } from '../../../../common/icons/cdl/ListDetails';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../../common/ui/Box';

interface CustomerAssignmentsSectionProps {
    customerId: string;
}

export const CustomerAssignmentsSection = ({ customerId }: CustomerAssignmentsSectionProps) => {
    const assignmentsQuery = useQuery(queries.assignments.list({ customerIds: [customerId] }));

    if (assignmentsQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (assignmentsQuery.isError) {
        return <AdvancedError />;
    }

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Text variant="subtitle1" as="h2">
                {translate('assignments.title')}
            </Text>

            {assignmentsQuery.data?.length ? (
                <TableBuilder<AssignmentGatewayMessage> data={assignmentsQuery.data} grow={false}>
                    <TableBuilderColumn<AssignmentGatewayMessage> header={translate('assignments.supplier')}>
                        {(row) => <AssignmentSupplier assignment={row} />}
                    </TableBuilderColumn>

                    <TableBuilderColumn<AssignmentGatewayMessage>
                        width="160px"
                        header={translate('assignments.status')}
                    >
                        {(row) => <AssignmentStatusBadge status={row.status} />}
                    </TableBuilderColumn>
                </TableBuilder>
            ) : (
                <StatePlaceholder
                    title={translate('assignments.noAssignmnents')}
                    description={translate('assignments.customerDetails.emptyStateDescription')}
                    Icon={IconListDetails}
                />
            )}
        </Box>
    );
};
